import { APP_INITIALIZER, NgModule, InjectionToken } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './components/login/login.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from './modules/shared/shared.module';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';
import { StoreModule, ActionReducer } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { SpinnerInterceptor } from './service/spinner.interceptor';
import { UserStatePersistenceService } from './service/user-state-persistence.service';
import { createUserReducerWithPersistence } from './store/User/user-reducer-with-persistence';
import { UserState } from './store/User/UserReducer';
import { UserActions } from './store/User/UserActions';
import { cashTransactionReducer } from './store/CashTransaction/CashTransactionReducer';
import { netOpenPositionReducer } from './store/NetOpenPosition/NetOpenPositionReducer';
import { portfolioStatusReducer } from './store/PortfolioStatus/PortfolioStatusReducer';
import { settlementRateReducer } from './store/SettlementRate/SettlementRateReducer';
import { HttpErrorInterceptorService } from './service/http-error-interceptor.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { ClearingWebInitService } from './service/clearing-web-init.service';
import { systemTypeReducer } from './store/SystemType/SystemTypeReducer';
import { CsrfInterceptor } from './service/csrf.interceptor';
import { tradeBlotterReducer } from './store/TradeBlotter/TradeBlotterReducer';

export function init(initService: ClearingWebInitService) {
  return (): Promise<void> => initService.initApp();
}

export const USER_REDUCER_TOKEN = new InjectionToken<
  ActionReducer<UserState, UserActions>
>('User Reducer');

export function userReducerFactory(
  persistenceService: UserStatePersistenceService,
): ActionReducer<UserState, UserActions> {
  return createUserReducerWithPersistence(persistenceService);
}

@NgModule({
  declarations: [AppComponent, LoginComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgxSpinnerModule,
    NgSelectModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ToastrModule.forRoot(),
    StoreModule.forRoot({}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
    }),
    StoreModule.forFeature('user', USER_REDUCER_TOKEN),
    StoreModule.forFeature('cashTransaction', cashTransactionReducer),
    StoreModule.forFeature('netOpenPosition', netOpenPositionReducer),
    StoreModule.forFeature('portfolioStatus', portfolioStatusReducer),
    StoreModule.forFeature('settlementRate', settlementRateReducer),
    StoreModule.forFeature('systemType', systemTypeReducer),
    StoreModule.forFeature('tradeBlotter', tradeBlotterReducer),
    FormsModule,
    SharedModule,
    NgxDatatableModule.forRoot({
      messages: {
        emptyMessage: 'No data to display',
        totalMessage: 'total',
        selectedMessage: 'selected',
      },
    }),
  ],
  providers: [
    UserStatePersistenceService,
    {
      provide: USER_REDUCER_TOKEN,
      useFactory: userReducerFactory,
      deps: [UserStatePersistenceService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SpinnerInterceptor,
      multi: true,
      deps: [NgxSpinnerService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CsrfInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: init,
      deps: [ClearingWebInitService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
